import React, { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import { useSelector } from "react-redux";
import {
  CircularProgress,
  Grid,
  Stack,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import classNames from "classnames";
import { makeStyles } from "@mui/styles";
import { useLocation, useNavigate } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ShapeOne from "../../assets/common/svg/shape-1.svg";
import ShapeTwo from "../../assets/common/svg/shape-2.svg";
import ShapeThree from "../../assets/common/svg/shape-3.svg";
import UserInfo from ".";
import VSNTypography from "../../shared/VSNTypography";
import BGImage from "../../assets/admin/assessmentResult/svg/bg-shape.png";
import Waivers from "./Waivers";
import Document from "./Document";
import Membership from "./Membership";
import VSNImage from "../../shared/VSNImage";
import BannerLayout from "../layout/BannerLayout";
import { getBannerInfo } from "../../description/common.description";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  mainAuth: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
      alignItems: "start",
    },
  },
  authDetail: {
    width: "100%",
    maxWidth: 595,
    margin: "0 auto",
    paddingRight: 32,
    [theme.breakpoints.down("md")]: {
      padding: [[0, 16, 0]],
      marginBottom: 47,
    },
  },
  shape: {
    position: "absolute",
  },
  shapeOne: {
    top: 80,
    left: -65,
    opacity: 0.4,
    zIndex: -1,
  },
  shapeTwo: {
    right: 0,
    top: "30%",
    transform: "translateY(-30%)",
    opacity: 0.9,
    zIndex: -1,
  },
  shapeThree: {
    left: 0,
    bottom: 40,
    opacity: 0.5,
    zIndex: -1,
  },
  usercontainer: {
    height: "calc(100vh - 75px)",
    overflowY: "auto",
    width: "100%",
    paddingBottom: 20,
    [theme.breakpoints.down("md")]: {
      height: "100%",
    },
  },
}));

function UserAccount(props) {
  const { window } = props;
  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const [selectedMenu, setSelectedMenu] = useState(
    queryParams?.get("activeTab") || "Overview"
  );

  const userRoleData = useSelector((state) => state.app?.userData);
  const { assessmentListForTakeAssessment: assessmentList } = useSelector(
    (state) => state.api
  );
  const signup_flow_completed =
    userRoleData?.user_profile?.signup_flow_completed;

  const remainingAssessment = assessmentList?.length
    ? assessmentList.find(
        (assessment) =>
          assessment.is_required_for_signup && !assessment.assessment_taken
      )
    : null;

  const isLoading = userRoleData === undefined || assessmentList === undefined;

  useEffect(() => {
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, []);

  const renderComponent = () => {
    // if (!signup_flow_completed && remainingAssessment) {
    //   return <UserInfo />;
    // }

    switch (selectedMenu) {
      case "Overview":
        return <UserInfo />;
      case "Documents":
        return <Document />;
      case "Waivers":
        return <Waivers />;
      // case "Membership":
      //   return <Membership />;
      default:
        return <UserInfo />;
    }
  };

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const drawer = (
    <>
      <List>
        {[
          "Overview",
          "Documents",
          "Waivers",
          //  "Membership"
        ]?.map((text) => (
          <ListItem key={text} disablePadding>
            <ListItemButton
              onClick={() => {
                setSelectedMenu(text);
                setMobileOpen(false);
              }}
              sx={{
                bgcolor: selectedMenu === text ? "#f0f0f0" : "transparent",
                "&:hover": { bgcolor: "#e0e0e0" },
              }}
            >
              <ListItemText primary={text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <>
      {userRoleData?.profile_updated ? (
        <Box sx={{ padding: { xs: "15px", md: "24px 56px" } }}>
          <Stack direction="row" alignItems="center" gap={2} mb={2} pl={2}>
            {!(!signup_flow_completed && remainingAssessment) && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  display: { sm: "none" },
                  backgroundColor: "var(--primaryLighter)",
                  borderRadius: "4px",
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <VSNTypography
              variant="h2"
              fontSize="34px"
              lineHeight="44px"
              fontWeight={700}
            >
              Account
            </VSNTypography>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{
              backgroundColor: "var(--white)",
              borderRadius: 4,
              boxShadow: "0px 0px 14px 0px #0000001A",
              overflow: "hidden",
              position: "relative",
              minHeight: "calc(100vh - 220px)",
              "&::after": {
                content: "''",
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: 320,
                background: `url(${BGImage})`,
              },
            }}
          >
            <CssBaseline />
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "calc(100vh - 220px)",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {signup_flow_completed || !remainingAssessment ? (
                  <Box
                    component="nav"
                    sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                    aria-label="sidebar"
                  >
                    <Drawer
                      container={container}
                      variant="temporary"
                      open={mobileOpen}
                      onTransitionEnd={handleDrawerTransitionEnd}
                      onClose={handleDrawerClose}
                      ModalProps={{
                        keepMounted: true,
                      }}
                      sx={{
                        display: { xs: "block", sm: "none" },
                        "& .MuiDrawer-paper": {
                          boxSizing: "border-box",
                          width: drawerWidth,
                          top: 64,
                          padding: "12px",
                        },
                      }}
                    >
                      {drawer}
                    </Drawer>
                    <Drawer
                      variant="permanent"
                      sx={{
                        display: { xs: "none", sm: "block", height: "100%" },
                        "& .MuiDrawer-paper": {
                          boxSizing: "border-box",
                          width: drawerWidth,
                          position: "relative",
                          padding: "12px",
                        },
                      }}
                      open
                    >
                      {drawer}
                    </Drawer>
                  </Box>
                ) : null}

                <Box
                  component="main"
                  sx={{
                    flexGrow: 1,
                    paddingX: { xs: "16px", md: "24px", lg: "48px" },
                    paddingY: { xs: "16px", md: "24px", lg: "24px" },
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {renderComponent()}
                </Box>
              </>
            )}
          </Stack>
        </Box>
      ) : (
        <Stack pt={4}>
          <Grid
            container
            {...(location?.pathname === "/edit-profile" && {
              alignItems: "flex-start",
            })}
            spacing={{ md: 4, xs: 3 }}
          >
            <Grid
              item
              xs={12}
              md={5}
              lg={4.4}
              xl={3.5}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              <BannerLayout {...getBannerInfo[location?.pathname]} />
            </Grid>
            <Grid
              item
              xs={12}
              md={7}
              lg={7.6}
              xl={8.5}
              position="relative"
              className={classes.mainAuth}
            >
              <Box
                className={classNames({
                  [classes.usercontainer]:
                    location?.pathname === "/edit-profile",
                })}
              >
                <Box className={classes.authDetail}>
                  <UserInfo />
                  <VSNImage
                    src={ShapeOne}
                    className={`${classes.shape} ${classes.shapeOne}`}
                    alt="ShapeOne"
                  />
                  <VSNImage
                    src={ShapeTwo}
                    className={`${classes.shape} ${classes.shapeTwo}`}
                    alt="ShapeTwo"
                  />
                  <VSNImage
                    src={ShapeThree}
                    className={`${classes.shape} ${classes.shapeThree}`}
                    alt="ShapeThree"
                  />{" "}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Stack>
      )}
    </>
  );
}

export default UserAccount;
